import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api, useAuth, useCart } from './AppContext';
import { formatCurrency } from '../i18n/formatters';
import { getActiveLocale } from '../i18n/activeLocale';

const ShopContext = createContext(null);

// ─────────────────────────────────────────────────────────────
// P0-F-1 — Isolation selectedShop staff vs customer
// Une clé localStorage dédiée par univers, plus de partage global.
// ─────────────────────────────────────────────────────────────
const SHOP_KEY_STAFF = 'superkawa_staff_shop';
const SHOP_KEY_CUSTOMER = 'superkawa_customer_shop';
const SHOP_KEY_LEGACY = 'superkawa_shop';
const STAFF_ROLES = ['barista', 'manager', 'owner'];

const isStaffRole = (role) => STAFF_ROLES.includes(role);
const resolveShopKey = (role) => (isStaffRole(role) ? SHOP_KEY_STAFF : SHOP_KEY_CUSTOMER);

// One-shot legacy migration : runs once à l'import du module.
// Copie superkawa_shop → clé du rôle courant si la cible est vide, puis supprime la legacy.
(function migrateLegacyShopKey() {
  try {
    const legacy = localStorage.getItem(SHOP_KEY_LEGACY);
    if (!legacy) return;
    const role = localStorage.getItem('superkawa_role') || 'customer';
    const targetKey = resolveShopKey(role);
    if (!localStorage.getItem(targetKey)) {
      localStorage.setItem(targetKey, legacy);
    }
    localStorage.removeItem(SHOP_KEY_LEGACY);
  } catch {
    // silencieux : si localStorage indispo on laisse passer
  }
})();

export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) throw new Error('useShop must be used within ShopProvider');
  return context;
};

export const ShopProvider = ({ children }) => {
  const { token, role, staffShopId } = useAuth();
  const { clearCart } = useCart();
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(() => {
    try {
      const key = resolveShopKey(role);
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  // Ref tracks latest selectedShop — never stale in callbacks
  const selectedShopRef = useRef(selectedShop);
  useEffect(() => {
    selectedShopRef.current = selectedShop;
  }, [selectedShop]);

  // Refs pour détecter transitions token/role (logout scoped)
  const prevTokenRef = useRef(token);
  const prevRoleRef = useRef(role);

  // Gère logout scoped + bascule de clé au changement de rôle
  useEffect(() => {
    const tokenWentNull = prevTokenRef.current && !token;
    const roleChanged = prevRoleRef.current !== role;

    if (tokenWentNull) {
      // Logout : clear UNIQUEMENT la clé du rôle qui se logue out
      try {
        localStorage.removeItem(resolveShopKey(prevRoleRef.current));
      } catch {}
    }

    if (tokenWentNull || roleChanged) {
      // Recharger selectedShop depuis la clé du (nouveau) rôle courant
      try {
        const saved = localStorage.getItem(resolveShopKey(role));
        const next = saved ? JSON.parse(saved) : null;
        setSelectedShop(next);
        selectedShopRef.current = next;
      } catch {
        setSelectedShop(null);
        selectedShopRef.current = null;
      }
    }

    prevTokenRef.current = token;
    prevRoleRef.current = role;
  }, [token, role]);

  const selectShop = useCallback((shop) => {
    // MB-UX-01 — Isolation panier multi-boutiques : tout changement EFFECTIF de
    // boutique (id différent) vide le panier pour interdire tout mélange A→B.
    // Le rafraîchissement avec le même id (fetchShops) ne déclenche aucun reset.
    const prevId = selectedShopRef.current?.id;
    const nextId = shop?.id;
    if (prevId && nextId && prevId !== nextId) {
      clearCart();
    }
    setSelectedShop(shop);
    selectedShopRef.current = shop;
    try {
      localStorage.setItem(resolveShopKey(role), JSON.stringify(shop));
    } catch {}
  }, [role, clearCart]);

  const fetchShops = useCallback(async () => {
    try {
      // MB-SIGNUP-2a — Isolation multi-tenant : un owner ne charge QUE ses propres
      // boutiques (scope=mine, filtré par appartenance backend). Aucun fallback global :
      // un nouvel owner reçoit une liste vide → débloque le Cas A onboarding et évite
      // toute fuite des boutiques publiques d'autres coffee shops.
      // Baristas/managers/clients conservent le comportement public existant (endpoint par défaut).
      // MB-SETTINGS-SECURITY-FIX — le token est désormais transmis pour TOUT rôle authentifié
      // (pas seulement owner) : le backend renvoie la fiche boutique complète uniquement au
      // staff réellement rattaché à cette boutique (cf. GET /stores, réponse à géométrie
      // variable) ; sans token, seule l'allowlist publique est renvoyée. Un token customer
      // n'élargit rien côté backend (le staff scoping ne s'applique qu'aux rôles STAFF_ROLES).
      const endpoint = role === 'owner' ? '/stores?scope=mine' : '/stores';
      const res = await api.get(endpoint, token || undefined);
      const list = res.data.stores || res.data || [];
      setShops(list);

      // Use ref to read the LATEST selection (not a stale closure)
      const current = selectedShopRef.current;

      if (current) {
        // Shop was previously selected — refresh it with fresh API data
        const fresh = list.find((s) => s.id === current.id);
        if (fresh) {
          selectShop(fresh);
        }
        // If the ID no longer exists (DB reset), clear selection
        // so ShopSelectionScreen shows
        if (!fresh) {
          setSelectedShop(null);
          selectedShopRef.current = null;
          try { localStorage.removeItem(resolveShopKey(role)); } catch {}
        }
      }
      // If nothing selected, leave null — ShopSelectionScreen will show
    } catch (err) {
      console.error('Failed to fetch shops:', err);
    } finally {
      setLoading(false);
    }
  }, [selectShop, role, token]);

  useEffect(() => {
    fetchShops();
  }, [fetchShops]);

  // P0-F-2b — Hydratation auto staff shop depuis JWT
  // Conditions : rôle staff + staffShopId présent + shops chargés + AUCUNE sélection en cours.
  // selectedShop n'est PAS dans les deps : on lit selectedShopRef pour éviter toute boucle.
  useEffect(() => {
    if (!isStaffRole(role)) return;
    if (!staffShopId) return;
    if (!shops.length) return;
    if (selectedShopRef.current) return; // respecte choix manuel multi-shop existant
    const target = shops.find((s) => s.id === staffShopId);
    if (target) selectShop(target);
  }, [role, staffShopId, shops, selectShop]);

  const clearShop = useCallback(() => {
    setSelectedShop(null);
    selectedShopRef.current = null;
    try { localStorage.removeItem(resolveShopKey(role)); } catch {}
  }, [role]);

  const currency = selectedShop?.currency_symbol || 'DA';

  const formatPrice = useCallback((amount) => {
    const sym = selectedShopRef.current?.currency_symbol || 'DA';
    const value = (!amount && amount !== 0) ? 0 : Math.round(amount);
    return formatCurrency(value, sym, getActiveLocale());
  }, []);

  const activeMethods = selectedShop?.payment_methods?.active || [];
  const futureMethods = selectedShop?.payment_methods?.future || [];

  return (
    <ShopContext.Provider value={{
      shops,
      selectedShop,
      selectShop,
      clearShop,
      loading,
      currency,
      formatPrice,
      activeMethods,
      futureMethods,
      refreshShops: fetchShops,
    }}>
      {children}
    </ShopContext.Provider>
  );
};
